<template>
  <ModalError />
  <div
    v-if="areSchemeChangesLoading || !isGetSchemeFinished"
    class="row justify-center q-mt-xl"
  >
    <q-spinner size="4em" color="primary" />
  </div>
  <div v-else>
    <div v-if="isCallOrApplicationPeriod" class="call-edit-page">
      <q-banner
        v-if="isEUPortalUpdateCopy"
        class="q-mt-md text-white bg-primary"
        rounded
      >
        This is a copy of an existing call with EU Portal updates applied. When you set its status from Draft
        to Live, these changes replace the original call, the “(EU Portal update - draft)” suffix is removed
        from the title, and the original call's documents are kept.
      </q-banner>
      <q-form @submit="handleFormSubmit" ref="callForm">
        <CopyCall class="q-mt-md" />
        <div class="inputs-box">
          <q-input
            class="q-mt-md"
            outlined
            v-model="scheme.selectedSchemeName"
            bg-color="white"
            label="Call name"
            stack-label
            :rules="requiredFieldRule"
            lazy-rules
            :disable="isEUCallJobScheme"
          />
          <q-select
            v-if="showParentSchemeSelect"
            class="q-mt-md"
            outlined
            bg-color="white"
            v-model="scheme.selectedParentScheme"
            :options="parentSchemesOptions"
            use-input
            option-value="id"
            option-label="label"
            stack-label
            label="Select parent scheme"
            @filter="filterParentSchemesFn"
            @update:model-value="
              val => (scheme.selectedSchemeParentSchemeId = val ? val.id : null)
            "
          />
          <q-input
            class="q-mt-md"
            outlined
            v-model="scheme.selectedSchemeIdentifier"
            bg-color="white"
            label="Call identifier"
            stack-label
            :disable="isCallOrApplicationPeriod && isEUCallJobScheme"
          />
          <q-input
            outlined
            v-model="scheme.selectedSchemeBudget"
            bg-color="white"
            label="Call budget"
            stack-label
            class="q-mt-md"
          />
          <q-select
            outlined
            bg-color="white"
            v-model="scheme.selectedSchemePublicationStatus"
            :options="scheme.publicationStatuses"
            class="q-mt-md scheme-inputs"
            option-value="name"
            option-label="name"
            stack-label
            label="Publication status"
          />
          <div class="application-periods-row">
            <div class="oneoff-application-period">
              <div class="row">
                <span class="app-period-heading"
                  >One-off application period</span
                >
                <q-checkbox
                  v-model="scheme.selectedSchemeIsOneOffDeadline"
                ></q-checkbox>
              </div>

              <q-input
                class="q-mt-md"
                outlined
                v-model="scheme.selectedSchemeCallStartDate"
                label="Start date"
                stack-label
                bg-color="white"
                :disable="!scheme.selectedSchemeIsOneOffDeadline"
              >
                <template v-slot:append>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="scheme.selectedSchemeCallStartDate"
                        mask="DD-MM-YYYY"
                      >
                        <div class="row items-center justify-end">
                          <q-btn
                            v-close-popup
                            label="Close"
                            color="primary"
                            flat
                          />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>

              <q-input
                class="q-mt-md"
                outlined
                v-model="scheme.selectedSchemeCallEndDate"
                label="End date"
                bg-color="white"
                stack-label
                :rules="requiredFieldRule"
                :disable="!scheme.selectedSchemeIsOneOffDeadline"
              >
                <template v-slot:append>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="scheme.selectedSchemeCallEndDate"
                        mask="DD-MM-YYYY"
                      >
                        <div class="row items-center justify-end">
                          <q-btn
                            v-close-popup
                            label="Close"
                            color="primary"
                            flat
                          />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>

            <div class="recurring-periods-row">
              <div class="column">
                <div class="row">
                  <span class="app-period-heading"
                    >Recurring application period</span
                  >
                  <q-checkbox
                    v-model="scheme.selectedSchemeHasRecurringDeadline"
                  >
                  </q-checkbox>
                </div>

                <div class="yearinput-recurring row q-mt-md">
                  Once per
                  <q-input
                    class="q-pl-sm"
                    style="width: 80px"
                    outlined
                    bg-color="white"
                    type="number"
                    v-model="scheme.selectedSchemeRecurringYearsDuration"
                    :disable="!scheme.selectedSchemeHasRecurringDeadline"
                    :rules="[requiredFieldRule]"
                  >
                  </q-input>
                  <span class="q-pl-sm">
                    year(s)
                  </span>
                </div>

                <div class="yearinput-recurring row q-mt-md">
                  Starting in
                  <q-input
                    class="q-pl-sm"
                    style="width: 100px"
                    outlined
                    bg-color="white"
                    type="number"
                    v-model="scheme.selectedSchemeRecurringStartingYear"
                    :disable="!scheme.selectedSchemeHasRecurringDeadline"
                    :rules="[requiredFieldRule]"
                  >
                  </q-input>
                </div>
              </div>
              <div class="column recurring-dates q-mt-md q-ml-lg">
                <q-input
                  class="q-mt-md"
                  outlined
                  v-model="scheme.selectedSchemeRecurringStartDate"
                  label="Start date"
                  stack-label
                  bg-color="white"
                  :disable="!scheme.selectedSchemeHasRecurringDeadline"
                  clearable
                >
                  <template v-slot:append>
                    <q-icon name="event" class="cursor-pointer">
                      <q-popup-proxy
                        ref="qDateProxy"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          v-model="scheme.selectedSchemeRecurringStartDate"
                          mask="DD-MM"
                        >
                          <div class="row items-center justify-end">
                            <q-btn
                              v-close-popup
                              label="Close"
                              color="primary"
                              flat
                            />
                          </div>
                        </q-date>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>

                <q-input
                  class="q-mt-md"
                  outlined
                  v-model="scheme.selectedSchemeRecurringEndDate"
                  label="End date"
                  stack-label
                  bg-color="white"
                  :disable="!scheme.selectedSchemeHasRecurringDeadline"
                >
                  <template v-slot:append>
                    <q-icon name="event" class="cursor-pointer">
                      <q-popup-proxy
                        ref="qDateProxy"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          v-model="scheme.selectedSchemeRecurringEndDate"
                          mask="DD-MM"
                        >
                          <div class="row items-center justify-end">
                            <q-btn
                              v-close-popup
                              label="Close"
                              color="primary"
                              flat
                            />
                          </div>
                        </q-date>
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
              </div>
            </div>

            <div class="row is-open">
              <q-checkbox v-model="scheme.selectedSchemeIsAlwaysOpen"
                >Is always open</q-checkbox
              >
            </div>
          </div>
        </div>
        <q-checkbox
          v-model="scheme.selectedSchemeIsEnglish"
          label="Is english call"
        />
        <div class="row" style="align-items: center;">
          <q-checkbox v-model="scheme.selectedSchemeIsCall" label="Is call" />
          <div class="text-caption q-mt-xs q-ml-md">
            This checkbox is set to true if this item is a call, otherwise it is
            an application period.
          </div>
        </div>
        <span>
          <p class="section-label q-mt-md">
            Body:
          </p>
        </span>
        <ckeditor
          :editor="editorRef.help.editor"
          v-model="scheme.selectedSchemeBody"
          :config="editorRef.help.editorConfig"
        ></ckeditor>
        <q-card
          v-if="scheme.selectedTransBody != null"
          class="my-card"
          flat
          bordered
        >
          <div
            style="padding-left:10px;"
            class="text-overline text-orange-9"
            @click="expanded = !expanded"
          >
            Show / hide translation
          </div>
          <q-slide-transition>
            <div v-show="expanded">
              <q-separator />
              <q-card-section class="text-subtitle2">
                <img :src="'https://flagsapi.com/NL/flat/32.png'" alt="Dutch" />
                <div v-html="scheme.selectedTransBody"></div>
              </q-card-section>
              <q-separator />
              <q-card-section
                class="text-subtitle2"
                v-if="scheme.selectedTransBodyDe"
              >
                <img
                  :src="'https://flagsapi.com/DE/flat/32.png'"
                  alt="German"
                />
                <div v-html="scheme.selectedTransBodyDe"></div>
              </q-card-section>
            </div>
          </q-slide-transition>
        </q-card>
        <div class="links-docs q-mt-lg">
          <div class="links-list scheme-inputs">
            <q-list bordered>
              <q-item-label class="tags-heading" header>
                Important links:
                <q-btn
                  color="primary"
                  @click="
                    showAddLink = true;
                    showCategories = false;
                  "
                  dense
                  no-caps
                >
                  <i class="las la-1x la-plus-circle la-fw"></i>
                  <q-tooltip>
                    Add new link
                  </q-tooltip>
                </q-btn>
              </q-item-label>
              <q-item v-for="link in selectedSchemeImportantLinks" :key="link">
                <q-item-section avatar>
                  <q-icon name="link" />
                </q-item-section>
                <q-item-section>
                  <q-item-label class="q-mt-xs link-text">
                    <span class="cursor-pointer">
                      <a :href="link.url" target="_blank">
                        {{ link.text + " (" + link.url + ")" }}
                      </a>
                    </span>
                  </q-item-label>
                </q-item-section>
                <q-item-section side>
                  <div class="q-gutter-xs">
                    <q-btn
                      color="red"
                      size="12px"
                      flat
                      dense
                      round
                      icon="delete"
                      @click="removeLink(link)"
                    >
                      <q-tooltip>
                        Remove link
                      </q-tooltip>
                    </q-btn>
                    <q-btn
                      @click="
                        showEditLink = true;
                        selectedLink = link;
                        showCategories = false;
                      "
                      dense
                      size="12px"
                      round
                      flat
                      color="indigo-10"
                      icon="las la-pencil-alt"
                    >
                      <q-tooltip>
                        Edit link
                      </q-tooltip>
                    </q-btn>
                  </div>
                </q-item-section>
              </q-item>
            </q-list>
          </div>
        </div>
        <AddLink
          :selectedSchemeLinks="scheme.selectedSchemeLinks"
          :showCategories="showCategories"
        />
        <AddFile :selectedSchemeDocuments="[]" />
        <SelectedTags />
        <div class="docs-and-other-links q-pb-sm">
          <q-list bordered separator>
            <q-item-label class="tags-heading" header>
              <div class="q-gutter-xs">
                Documents and other links:
                <q-btn
                  color="primary"
                  @click="
                    showAddLink = true;
                    showCategories = true;
                  "
                  dense
                  no-caps
                >
                  <i class="las la-1x la-link la-fw"></i>
                  <q-tooltip>
                    Add new link
                  </q-tooltip>
                </q-btn>
                <q-btn
                  color="primary"
                  @click="showAddDocument = true"
                  dense
                  no-caps
                >
                  <i class="las la-1x la-paperclip la-fw"></i>
                  <q-tooltip>
                    Add new document
                  </q-tooltip>
                </q-btn>
              </div>
            </q-item-label>
            <q-item
              v-ripple
              v-for="item in sortedSchemeDocsAndLinks"
              :key="item"
            >
              <template v-if="'extension' in item && 'name' in item">
                <q-item-section side style="width:150px;">
                  {{ item.documentTypeDescription }}
                </q-item-section>
                <q-item-section avatar>
                  <i
                    :class="iconBasedOnExtension(item.extension)"
                    style="color: blue;"
                  ></i>
                </q-item-section>
                <q-item-section>{{ nameSubstring(item.name) }}</q-item-section>
                <q-item-section top side>
                  <q-btn
                    class="gt-xs"
                    size="12px"
                    flat
                    dense
                    round
                    icon="delete"
                    color="red"
                    @click="
                      confirmRemovingFile = true;
                      documentId = item.id;
                    "
                  >
                    <q-tooltip>Remove file</q-tooltip>
                  </q-btn>
                </q-item-section>
                <q-item-section top side>
                  <q-btn
                    class="gt-xs"
                    size="12px"
                    flat
                    dense
                    round
                    icon="download"
                    color="secondary"
                    @click="download(item.id, item.name)"
                  >
                    <q-tooltip>Download file</q-tooltip>
                  </q-btn>
                </q-item-section>
                <q-item-section top side>
                  <q-btn
                    class="gt-xs"
                    size="12px"
                    flat
                    dense
                    round
                    icon="las la-pencil-alt"
                    color="indigo-10"
                    @click="
                      showEditDocument = true;
                      scheme.selectedDocument = item;
                      updateSchemeSelectedDocument(item);
                    "
                  >
                    <q-tooltip>Edit document</q-tooltip>
                  </q-btn>
                </q-item-section>
                <q-tooltip>{{ item.name }}</q-tooltip>
              </template>
              <template v-if="'url' in item && 'text' in item">
                <q-item-section side style="width:150px;">
                  {{ item.documentTypeDescription }}
                </q-item-section>
                <q-item-section avatar>
                  <i class="las la-2x la-link" style="color: blue;"></i>
                </q-item-section>
                <q-item-section>
                  <a :href="item.url" target="_blank">
                    {{ item.text + " (" + item.url + ")" }}
                  </a>
                </q-item-section>
                <q-item-section top side>
                  <q-btn
                    class="gt-xs"
                    size="12px"
                    flat
                    dense
                    round
                    icon="delete"
                    color="red"
                    @click="removeLink(item)"
                  >
                    <q-tooltip>Remove link</q-tooltip>
                  </q-btn>
                </q-item-section>
                <q-item-section top side>
                  <q-btn
                    class="gt-xs"
                    size="12px"
                    flat
                    dense
                    round
                    icon="las la-pencil-alt"
                    color="indigo-10"
                    @click="
                      showEditLink = true;
                      selectedLink = item;
                      showCategories = true;
                    "
                  >
                    <q-tooltip>Edit link</q-tooltip>
                  </q-btn>
                </q-item-section>
              </template>
            </q-item>
          </q-list>
        </div>
        <q-dialog v-model="confirmRemovingFile" persistent>
          <q-card>
            <q-card-section class="row items-center">
              <q-avatar icon="delete" color="primary" text-color="white" />
              <span class="q-ml-sm"
                >Do you want to remove this file from the call?</span
              >
            </q-card-section>

            <q-card-actions align="right">
              <q-btn flat label="Cancel" color="primary" v-close-popup />
              <q-btn
                flat
                label="Delete"
                color="primary"
                v-close-popup
                @click="removeDocument(documentId)"
              />
            </q-card-actions>
          </q-card>
        </q-dialog>
        <div class="row justify-end submit-btn-container">
          <q-btn
            type="submit"
            class="q-mb-lg btn-vind-dark"
            label="Submit changes"
          />
        </div>
        <EditLink v-if="selectedLink" :showCategories="showCategories" />
        <EditDocument v-if="scheme.selectedDocument" />
      </q-form>
    </div>
    <div v-else class="scheme-edit-page">
      <q-form @submit="handleFormSubmit" ref="schemeForm">
        <div class="row form-heading">
          <div class="col-xs-12 col-lg-6 first-head-col">
            <q-input
              borderless
              v-model="scheme.selectedSchemeName"
              bg-color="white"
              label="Name"
              stack-label
              :rules="requiredFieldRule"
              lazy-rules
            />
          </div>
          <div class="col-xs-12 col-sm-9 col-lg-4 second-head-col">
            <div class="row justify-between">
              <div class="col-sm-4">
                <div>
                  <span>Complexity</span>
                </div>
                <q-rating
                  v-model="scheme.selectedSchemeComplexityRating"
                  size="sm"
                  :max="5"
                  color="primary"
                  icon="star_border"
                  icon-selected="star"
                ></q-rating>
              </div>
              <div class="col-sm-4">
                <div>
                  <span>Success rate</span>
                </div>
                <q-rating
                  v-model="scheme.selectedSchemeChanceOfSuccessRating"
                  size="sm"
                  :max="5"
                  color="primary"
                  icon="star_border"
                  icon-selected="star"
                ></q-rating>
              </div>
              <div class="col-sm-4">
                <div>
                  <span>Application duration</span>
                </div>
                <q-rating
                  v-model="scheme.selectedSchemeTimeForApplicationRating"
                  size="sm"
                  :max="5"
                  color="primary"
                  icon="star_border"
                  icon-selected="star"
                ></q-rating>
              </div>
            </div>
          </div>
          <div class="col-xs-12 col-sm-3 col-lg-2 column third-head-col">
            <CopyScheme />
            <CreateCall :parentSchemeName="scheme.selectedSchemeName" />
          </div>
        </div>
        <div class="row justify-between">
          <div class="col-xs-12 col-md-6 col-lg-3 first-col">
            <q-input
              borderless
              v-model="scheme.selectedSchemeIdentifier"
              bg-color="white"
              label="Identifier"
              stack-label
              disable
            />
            <q-input
              class="q-mt-md"
              borderless
              v-model="scheme.selectedSchemeAccronym"
              bg-color="white"
              label="Accronym"
              stack-label
              :rules="maxLengthRule"
              lazy-rules
            />
            <q-input
              borderless
              v-model="scheme.selectedSchemeArchivecode"
              bg-color="white"
              label="Archive code"
              stack-label
              :rules="maxLengthRule"
              lazy-rules
            />
            <q-input
              v-model.number="scheme.selectedSchemeMinAmount"
              type="number"
              min="0"
              label="Minimum amount"
              stack-label
              bg-color="white"
              borderless
            />
            <q-input
              class="q-mt-md"
              v-model.number="scheme.selectedSchemeMaxAmount"
              type="number"
              min="0"
              label="Maximum amount"
              bg-color="white"
              stack-label
              borderless
            />
            <div class="date-input q-mt-md">
              <q-input
                borderless
                bg-color="white"
                v-model="scheme.selectedSchemeStartDate"
                label="Start date"
                stack-label
                class="date-input"
              >
                <template v-slot:append>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="scheme.selectedSchemeStartDate"
                        mask="DD-MM-YYYY"
                      >
                        <div class="row items-center justify-end">
                          <q-btn
                            v-close-popup
                            label="Close"
                            color="primary"
                            flat
                          />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
            <div class="date-input q-mt-md">
              <q-input
                borderless
                class="date-input"
                bg-color="white"
                v-model="scheme.selectedSchemeEndDate"
                label="End date"
                stack-label
              >
                <template v-slot:append>
                  <q-icon name="event" class="cursor-pointer">
                    <q-popup-proxy
                      ref="qDateProxy"
                      transition-show="scale"
                      transition-hide="scale"
                    >
                      <q-date
                        v-model="scheme.selectedSchemeEndDate"
                        mask="DD-MM-YYYY"
                      >
                        <div class="row items-center justify-end">
                          <q-btn
                            v-close-popup
                            label="Close"
                            color="primary"
                            flat
                          />
                        </div>
                      </q-date>
                    </q-popup-proxy>
                  </q-icon>
                </template>
              </q-input>
            </div>
            <div class="other-select q-mt-md" style="min-width: 250px">
              <q-select
                borderless
                bg-color="white"
                v-model="scheme.selectedSchemeScope"
                :options="scopes"
                class="scheme-inputs"
                option-value="id"
                option-label="description"
                stack-label
                label="Scheme scope"
                emit-value
                map-options
              />
            </div>
          </div>
          <div class="col-xs-12 col-md-6 col-lg-3 second-col">
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemeTerm"
              :options="scheme.applicationTerms"
              class="scheme-inputs"
              option-value="name"
              option-label="name"
              stack-label
              label="Application term"
            />
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemePublicationStatus"
              :options="scheme.publicationStatuses"
              class="q-mt-md scheme-inputs"
              option-value="name"
              option-label="name"
              stack-label
              label="Publication status"
            />
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemeStatus"
              :options="schemeStatusOptions"
              :display-value="displayStatus"
              class="q-mt-md scheme-inputs"
              option-value="value"
              option-label="label"
              option-disable="disable"
              stack-label
              label="Status"
              emit-value
              map-options
            />
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemeFundingOrgs"
              multiple
              :options="filterOrganisationsFundingOptions"
              use-chips
              use-input
              option-value="name"
              class="q-mt-md scheme-inputs"
              option-label="name"
              stack-label
              label="Select funding organisation(s)"
              @filter="filterFundingOrganisationsFn"
              :rules="requiredFieldRule"
              @update:model-value="setInput()"
              ref="fundingOrgSelect"
              popup-content-class="ellipsis-options"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <div class="q-item__label ellipsis-text">
                      {{ scope.opt.name }}
                      <q-tooltip anchor="top middle" self="bottom middle">
                        {{ scope.opt.name }}
                      </q-tooltip>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemeAdministratingOrgs"
              multiple
              :options="filterOrganisationsAdministratingOptions"
              use-chips
              use-input
              option-value="name"
              class="q-mt-sm scheme-inputs"
              option-label="name"
              stack-label
              label="Select administrating organisation(s)"
              @filter="filterAdministratingOrganisationsFn"
              :rules="requiredFieldRule"
              @update:model-value="setInput()"
              ref="adminOrgsSelect"
              popup-content-class="ellipsis-options"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <div class="q-item__label ellipsis-text">
                      {{ scope.opt.name }}
                      <q-tooltip anchor="top middle" self="bottom middle">
                        {{ scope.opt.name }}
                      </q-tooltip>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemeAdvisingOrgs"
              multiple
              :options="filterOrganisationsAdvisingOptions"
              use-chips
              use-input
              option-value="name"
              class="q-mt-md scheme-inputs"
              option-label="name"
              stack-label
              label="Select advising organisation(s)"
              @filter="filterAdvisingOrganisationsFn"
              @update:model-value="setInput()"
              ref="advisingOrgsSelect"
              popup-content-class="ellipsis-options"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <div class="q-item__label ellipsis-text">
                      {{ scope.opt.name }}
                      <q-tooltip anchor="top middle" self="bottom middle">
                        {{ scope.opt.name }}
                      </q-tooltip>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemeRecipientOrgs"
              multiple
              :options="filterOrganisationsRecipientOptions"
              use-chips
              use-input
              option-value="name"
              class="q-mt-sm scheme-inputs"
              option-label="name"
              stack-label
              label="Select recipient organisation(s)"
              @filter="filterRecipientOrganisationsFn"
              @update:model-value="setInput()"
              ref="recipientOrgsSelect"
              popup-content-class="ellipsis-options"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <div class="q-item__label ellipsis-text">
                      {{ scope.opt.name }}
                      <q-tooltip anchor="top middle" self="bottom middle">
                        {{ scope.opt.name }}
                      </q-tooltip>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-input
              class="q-mt-sm"
              borderless
              v-model="scheme.selectedSchemeDuration"
              bg-color="white"
              label="Duration"
              stack-label
            />
            <q-select
              borderless
              bg-color="white"
              v-model="scheme.selectedSchemeSDGThemes"
              multiple
              :options="filterThemesSDGOptions"
              use-chips
              use-input
              class="q-mt-sm scheme-inputs"
              stack-label
              label="Select SDG theme(s)"
              @filter="filterThemesSDGFn"
              ref="sdgThemeSelect"
              @update:model-value="setInput()"
              popup-content-class="ellipsis-options"
            >
              <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                  <q-item-section>
                    <div class="q-item__label ellipsis-text">
                      {{ scope.opt }}
                      <q-tooltip anchor="top middle" self="bottom middle">
                        {{ scope.opt }}
                      </q-tooltip>
                    </div>
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>
          <div class="col-xs-12 col-md-4 col-lg-2 column third-col">
            <q-checkbox
              v-model="scheme.selectedSchemeIsPortal"
              label="Is portal scheme"
            />
            <q-checkbox
              v-model="scheme.selectedSchemeIsScience"
              label="Is science scheme"
            />
            <q-checkbox
              v-model="scheme.selectedSchemeIsEnglish"
              label="Is english scheme"
            />
            <q-checkbox
              v-model="scheme.selectedSchemeHasEnglishCalls"
              label="Has english calls"
            />
            <q-checkbox
              v-model="scheme.selectedSchemeIsOBScheme"
              label="Is OB scheme"
            />
            <p class="ob-hint" v-show="scheme.selectedSchemeIsOBScheme">
              <i
                >If deselected values from below will be removed with next
                save.</i
              >
            </p>
            <q-input
              v-if="scheme.selectedSchemeIsOBScheme"
              borderless
              v-model="scheme.selectedSchemeOBTitle"
              bg-color="white"
              label="Scheme OB title"
              stack-label
              :rules="maxLengthRule"
              lazy-rules
              style="min-width: 240px"
            />
            <q-input
              v-if="scheme.selectedSchemeIsOBScheme"
              borderless
              v-model="scheme.selectedSchemeOBDescription"
              bg-color="white"
              label="Scheme OB description"
              stack-label
              lazy-rules
              style="min-width: 240px"
              type="textarea"
            />
          </div>
          <div class="col-xs-12 col-md-8 col-lg-4 fourth-col">
            <q-separator class="q-mb-md" />
            <p class="section-label q-mt-lg">
              Exclusions:
            </p>
            <q-separator class="separator" />
            <div class="links-list scheme-inputs">
              <q-list bordered>
                <q-item-label class="tags-heading" header>
                  Important links:
                  <q-btn
                    color="primary"
                    @click="
                      showAddLink = true;
                      showCategories = false;
                    "
                    dense
                    no-caps
                  >
                    <i class="las la-1x la-plus-circle la-fw"></i>
                    <q-tooltip>
                      Add new link
                    </q-tooltip>
                  </q-btn>
                </q-item-label>
                <q-item
                  v-for="link in selectedSchemeImportantLinks"
                  :key="link"
                >
                  <q-item-section avatar>
                    <q-icon name="link" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="q-mt-xs link-text">
                      <span class="cursor-pointer">
                        <a :href="link.url" target="_blank">
                          {{ link.text + " (" + link.url + ")" }}
                        </a>
                      </span>
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side>
                    <div class="q-gutter-xs">
                      <q-btn
                        color="red"
                        size="12px"
                        flat
                        dense
                        round
                        icon="delete"
                        @click="removeLink(link)"
                      >
                        <q-tooltip>
                          Remove link
                        </q-tooltip>
                      </q-btn>
                      <q-btn
                        @click="
                          showEditLink = true;
                          selectedLink = link;
                          showCategories = false;
                        "
                        dense
                        size="12px"
                        round
                        flat
                        color="indigo-10"
                        icon="las la-pencil-alt"
                      >
                        <q-tooltip>
                          Edit link
                        </q-tooltip>
                      </q-btn>
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </div>
          </div>
        </div>
        <div class="column">
          <div class="section">
            <q-separator class="separator" />
            <p class="section-lable">
              ICTU:
            </p>
            <ICTUSection
              v-model:ICTUscheme="scheme.selectedSchemeICTU"
              v-model:schemeMinimalAge="scheme.selectedSchemeMinimalAge"
              v-model:schemeMaximalAge="scheme.selectedSchemeMaximalAge"
              :educationLevelPairs="scheme.selectedSchemeEducationLevelTags"
              :userStatusPairs="scheme.selectedSchemeUserStatusTags"
            />
            <q-separator class="separator" />
          </div>
          <div class="section">
            <p class="section-label">
              Introduction:
            </p>
            <q-input
              class="q-mt-md"
              borderless
              v-model="scheme.selectedSchemeIntroduction"
              bg-color="white"
              type="textarea"
            />
            <q-card
              v-if="scheme.selectedTransIntroduction != null"
              class="my-card"
              flat
              bordered
            >
              <div
                style="padding-left:10px;"
                class="text-overline text-orange-9"
                @click="expanded = !expanded"
              >
                Show / hide translation
              </div>
              <q-slide-transition>
                <div class="translation-box" v-show="expanded">
                  <q-separator />
                  <q-card-section class="text-subtitle2">
                    <img
                      :src="'https://flagsapi.com/NL/flat/32.png'"
                      alt="Dutch"
                    />

                    <div v-html="scheme.selectedTransIntroduction"></div>
                  </q-card-section>
                  <q-separator />
                  <q-card-section
                    class="text-subtitle2"
                    v-if="scheme.selectedTransIntroductionDe"
                  >
                    <img
                      :src="'https://flagsapi.com/DE/flat/32.png'"
                      alt="German"
                    />

                    <div v-html="scheme.selectedTransIntroductionDe"></div>
                  </q-card-section>
                </div>
              </q-slide-transition>
            </q-card>
          </div>
          <div class="section">
            <p class="section-label">
              Body:
            </p>
            <ckeditor
              :editor="editorRef.help.editor"
              v-model="scheme.selectedSchemeBody"
              :config="editorRef.help.editorConfig"
            >
            </ckeditor>
            <q-card
              v-if="scheme.selectedTransBody != null"
              class="my-card"
              flat
              bordered
            >
              <div
                style="padding-left:10px;"
                class="text-overline text-orange-9"
                @click="expanded = !expanded"
              >
                Show / hide translation
              </div>
              <q-slide-transition>
                <div class="translation-box" v-show="expanded">
                  <q-separator />
                  <q-card-section class="text-subtitle2">
                    <img
                      :src="'https://flagsapi.com/NL/flat/32.png'"
                      alt="Dutch"
                    />
                    <div v-html="scheme.selectedTransBody"></div>
                  </q-card-section>
                  <q-separator />
                  <q-card-section
                    class="text-subtitle2"
                    v-if="scheme.selectedTransBodyDe"
                  >
                    <img
                      :src="'https://flagsapi.com/DE/flat/32.png'"
                      alt="German"
                    />
                    <div v-html="scheme.selectedTransBodyDe"></div>
                  </q-card-section>
                </div>
              </q-slide-transition>
            </q-card>
          </div>
          <div class="section">
            <p class="section-label">
              Application information:
            </p>
            <ckeditor
              :editor="editorRef.help.editor"
              v-model="scheme.selectedSchemeApplicationInformation"
              :config="editorRef.help.editorConfig"
            ></ckeditor>
            <q-card
              v-if="scheme.selectedTransApplicationInformation != null"
              class="my-card"
              flat
              bordered
            >
              <div
                style="padding-left:10px;"
                class="text-overline text-orange-9"
                @click="expanded = !expanded"
              >
                Show / hide translation
              </div>
              <q-slide-transition>
                <div v-show="expanded">
                  <q-separator />
                  <q-card-section class="text-subtitle2">
                    <img
                      :src="'https://flagsapi.com/NL/flat/32.png'"
                      alt="Dutch"
                    />

                    <div
                      v-html="scheme.selectedTransApplicationInformation"
                    ></div>
                  </q-card-section>
                  <q-separator />

                  <q-card-section
                    class="text-subtitle2"
                    v-if="scheme.selectedTransApplicationInformationDe"
                  >
                    <img
                      :src="'https://flagsapi.com/DE/flat/32.png'"
                      alt="German"
                    />
                    <div
                      v-html="scheme.selectedTransApplicationInformationDe"
                    ></div>
                  </q-card-section>
                </div>
              </q-slide-transition>
            </q-card>
          </div>
          <div class="section">
            <p class="section-label">
              Application procedure:
            </p>
            <q-input
              class="q-mt-md applicationprocedure-textarea"
              borderless
              v-model="scheme.selectedSchemeApplicationProcedure"
              bg-color="white"
              type="textarea"
            />
            <q-card
              v-if="scheme.selectedTransApplicationProcedure != null"
              class="my-card"
              flat
              bordered
            >
              <div
                style="padding-left:10px;"
                class="text-overline text-orange-9"
                @click="expanded = !expanded"
              >
                Show / hide translation
              </div>
              <q-slide-transition>
                <div class="translation-box" v-show="expanded">
                  <q-separator />
                  <q-card-section class="text-subtitle2">
                    <img
                      :src="'https://flagsapi.com/NL/flat/32.png'"
                      alt="Dutch"
                    />

                    <div
                      v-html="scheme.selectedTransApplicationProcedure"
                    ></div>
                  </q-card-section>
                  <q-separator />

                  <q-card-section
                    class="text-subtitle2"
                    v-if="scheme.selectedTransApplicationProcedureDe"
                  >
                    <img
                      :src="'https://flagsapi.com/DE/flat/32.png'"
                      alt="German"
                    />
                    <div
                      v-html="scheme.selectedTransApplicationProcedureDe"
                    ></div>
                  </q-card-section>
                </div>
              </q-slide-transition>
            </q-card>
          </div>
          <div class="section">
            <p class="section-label">
              Criteria:
            </p>
            <ckeditor
              :editor="editorRef.help.editor"
              v-model="scheme.selectedSchemeCriteria"
              :config="editorRef.help.editorConfig"
            >
            </ckeditor>
            <q-card
              v-if="scheme.selectedTransCriteria != null"
              class="my-card"
              flat
              bordered
            >
              <div
                style="padding-left:10px;"
                class="text-overline text-orange-9"
                @click="expanded = !expanded"
              >
                Show / hide translation
              </div>
              <q-slide-transition>
                <div class="translation-box" v-show="expanded">
                  <q-separator />
                  <q-card-section class="text-subtitle2">
                    <img
                      :src="'https://flagsapi.com/NL/flat/32.png'"
                      alt="Dutch"
                    />

                    <div v-html="scheme.selectedTransCriteria"></div>
                  </q-card-section>
                  <q-separator />
                  <q-card-section
                    class="text-subtitle2"
                    v-if="scheme.selectedTransCriteriaDe"
                  >
                    <img
                      :src="'https://flagsapi.com/DE/flat/32.png'"
                      alt="German"
                    />

                    <div v-html="scheme.selectedTransCriteriaDe"></div>
                  </q-card-section>
                </div>
              </q-slide-transition>
            </q-card>
          </div>
          <div class="section">
            <p class="section-label">
              Budget:
            </p>
            <q-input
              class="q-mt-md budget-textarea"
              borderless
              v-model="scheme.selectedSchemeBudget"
              bg-color="white"
              type="textarea"
            />
          </div>
          <div class="section">
            <p class="section-label">
              Financial information:
            </p>
            <ckeditor
              :editor="editorRef.help.editor"
              v-model="scheme.selectedSchemeFinancialInformation"
              :config="editorRef.help.editorConfig"
            ></ckeditor>
            <q-card
              v-if="scheme.transFinancialinformation != null"
              class="my-card"
              flat
              bordered
            >
              <div
                style="padding-left:10px;"
                class="text-overline text-orange-9"
                @click="expanded = !expanded"
              >
                Show / hide translation
              </div>
              <q-slide-transition>
                <div class="translation-box" v-show="expanded">
                  <q-separator />
                  <q-card-section class="text-subtitle2">
                    <div v-html="scheme.transFinancialinformation"></div>
                  </q-card-section>
                </div>
              </q-slide-transition>
            </q-card>
          </div>
          <div class="section">
            <p class="section-label">
              Portal body:
            </p>
            <ckeditor
              :editor="editorRef.help.editor"
              v-model="scheme.selectedSchemePortalBody"
              :config="editorRef.help.editorConfig"
            >
            </ckeditor>
          </div>
          <div class="section">
            <SelectedTags />
          </div>
          <div class="section">
            <q-list class="docs-and-links" bordered separator>
              <q-item-label class="tags-heading" header>
                <div class="q-gutter-xs">
                  Documents and other links:
                  <q-btn
                    color="primary"
                    @click="
                      showAddLink = true;
                      showCategories = true;
                    "
                    dense
                    no-caps
                  >
                    <i class="las la-1x la-link la-fw"></i>
                    <q-tooltip>
                      Add new link
                    </q-tooltip>
                  </q-btn>
                  <q-btn
                    color="primary"
                    @click="showAddDocument = true"
                    dense
                    no-caps
                  >
                    <i class="las la-1x la-paperclip la-fw"></i>
                    <q-tooltip>
                      Add new document
                    </q-tooltip>
                  </q-btn>
                </div>
              </q-item-label>
              <q-item
                v-ripple
                v-for="item in sortedSchemeDocsAndLinks"
                :key="item"
              >
                <template v-if="'extension' in item && 'name' in item">
                  <q-item-section side style="width:150px;">
                    {{ item.documentTypeDescription }}
                  </q-item-section>
                  <q-item-section avatar>
                    <i
                      :class="iconBasedOnExtension(item.extension)"
                      style="color: blue;"
                    ></i>
                  </q-item-section>
                  <q-item-section>{{
                    nameSubstring(item.name)
                  }}</q-item-section>
                  <q-item-section top side>
                    <q-btn
                      class="gt-xs"
                      size="12px"
                      flat
                      dense
                      round
                      icon="delete"
                      color="red"
                      @click="
                        confirmRemovingFile = true;
                        documentId = item.id;
                      "
                    >
                      <q-tooltip>Remove file</q-tooltip>
                    </q-btn>
                  </q-item-section>
                  <q-item-section top side>
                    <q-btn
                      class="gt-xs"
                      size="12px"
                      flat
                      dense
                      round
                      icon="download"
                      color="secondary"
                      @click="download(item.id, item.name)"
                    >
                      <q-tooltip>Download file</q-tooltip>
                    </q-btn>
                  </q-item-section>
                  <q-item-section top side>
                    <q-btn
                      class="gt-xs"
                      size="12px"
                      flat
                      dense
                      round
                      icon="las la-pencil-alt"
                      color="indigo-10"
                      @click="
                        showEditDocument = true;
                        scheme.selectedDocument = item;
                        updateSchemeSelectedDocument(item);
                      "
                    >
                      <q-tooltip>Edit document</q-tooltip>
                    </q-btn>
                  </q-item-section>
                  <q-tooltip>{{ item.name }}</q-tooltip>
                </template>
                <template v-if="'url' in item && 'text' in item">
                  <q-item-section side style="width:150px;">
                    {{ item.documentTypeDescription }}
                  </q-item-section>
                  <q-item-section avatar>
                    <i class="las la-2x la-link" style="color: blue;"></i>
                  </q-item-section>
                  <q-item-section>
                    <a :href="item.url" target="_blank">
                      {{ item.text + " (" + item.url + ")" }}
                    </a>
                  </q-item-section>
                  <q-item-section top side>
                    <q-btn
                      class="gt-xs"
                      size="12px"
                      flat
                      dense
                      round
                      icon="delete"
                      color="red"
                      @click="removeLink(item)"
                    >
                      <q-tooltip>Remove link</q-tooltip>
                    </q-btn>
                  </q-item-section>
                  <q-item-section top side>
                    <q-btn
                      class="gt-xs"
                      size="12px"
                      flat
                      dense
                      round
                      icon="las la-pencil-alt"
                      color="indigo-10"
                      @click="
                        showEditLink = true;
                        selectedLink = item;
                        showCategories = true;
                      "
                    >
                      <q-tooltip>Edit link</q-tooltip>
                    </q-btn>
                  </q-item-section>
                </template>
              </q-item>
            </q-list>
            <AddLink
              :selectedSchemeLinks="scheme.selectedSchemeLinks"
              :showCategories="showCategories"
            />
            <AddFile :selectedSchemeDocuments="[]" />
          </div>
          <div class="section">
            <SchemeUpdates :schemeUpdates="schemeUpdates" action="Editing" />
          </div>
          <div class="section">
            <div class="call-create-section">
              <CreateCall :parentSchemeName="scheme.selectedSchemeName" />
            </div>
          </div>
          <div class="section">
            <CallsAndApplicationPeriods />
          </div>
          <div class="row justify-end">
            <q-btn
              type="submit"
              class="q-mb-xl q-mt-lg orange-btn-filled bold-font"
              no-caps
              label="Submit changes"
            />
          </div>
        </div>
      </q-form>
      <q-dialog v-model="confirmRemovingFile" persistent>
        <q-card>
          <q-card-section class="row items-center">
            <q-avatar icon="delete" color="primary" text-color="white" />
            <span class="q-ml-sm"
              >Do you want to remove this file from scheme?</span
            >
          </q-card-section>

          <q-card-actions align="right">
            <q-btn flat label="Cancel" color="primary" v-close-popup />
            <q-btn
              flat
              label="Delete"
              color="primary"
              v-close-popup
              @click="removeDocument(documentId)"
            />
          </q-card-actions>
        </q-card>
      </q-dialog>
      <q-dialog v-model="showSuccessfullyCreatedCallDialog" persistent>
        <q-card style="width: 300px">
          <q-card-section class="row items-center">
            <span>Successfully created!</span>
          </q-card-section>
          <q-card-actions align="right" class="bg-white text-teal">
            <q-btn flat label="OK" @click="disableDialog()" />
          </q-card-actions>
        </q-card>
      </q-dialog>
      <EditLink v-if="selectedLink" :showCategories="showCategories" />
      <EditDocument v-if="scheme.selectedDocument" />
    </div>
  </div>
  <q-dialog v-model="showDiscontinuedModal" persistent>
    <q-card style="width: 300px">
      <q-card-section>
        <div class="text-h6">Warning</div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        Are you sure you want to set this scheme, including all childs, to
        discontinued?
      </q-card-section>

      <q-card-actions align="right" class="bg-white text-teal">
        <q-btn flat label="Yes" @click="setDiscontinued()" />
        <q-btn flat label="No" @click="disableWarning()" />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import SchemesServices from "../../services/schemes.services";
import TagsService from "../../services/tags.service";
import OrganisationsService from "../../services/organisations.service";
import NotificationService from "../../services/notification.service";
import { onMounted, onBeforeUnmount, computed, watch, ref } from "vue";
import store from "../../store/index";
import { useRoute, useRouter } from "vue-router";
import moment from "moment";
import ModalError from "../schemes-components/SchemeModalError.vue";
import CallsAndApplicationPeriods from "./CallsAndApplicationPeriods.vue";
import ICTUSection from "./ICTUSection.vue";
import SchemeUpdates from "./SchemeUpdates.vue";
import SelectedTags from "./SelectedTags.vue";
import CopyScheme from "./CopyScheme.vue";
import CreateCall from "./CreateCall.vue";
import CopyCall from "./CopyCall.vue";
import AddLink from "./AddLink.vue";
import EditLink from "./EditLink.vue";
import AddFile from "./AddFile.vue";
import EditDocument from "./EditDocument.vue";
import help from "../../helpers/editor";
import ETagType from "../../enums/ETagType";
import ESchemeStatus from "../../enums/ESchemeStatus";
import EDocumentType from "../../enums/EDocumentType";

export default {
  name: "EditScheme",
  components: {
    ModalError,
    CallsAndApplicationPeriods,
    ICTUSection,
    CreateCall,
    SchemeUpdates,
    SelectedTags,
    CopyScheme,
    CopyCall,
    AddLink,
    EditLink,
    AddFile,
    EditDocument
  },
  setup() {
    const AUTOSAVE_INTERVAL_MIN = 10;
    const AUTOSAVE_INTERVAL_MILLISECONDS = AUTOSAVE_INTERVAL_MIN * 60 * 1000;
    const CREATE_EU_CALLS_JOB = "CreateAndUpdateEUCallsJob";
    const route = useRoute();
    const router = useRouter();
    const editorRef = help;
    const requiredFieldRule = [
      val => (val && val.length > 0) || "Field is required!"
    ];
    const maxLengthRule = [
      val => val.length < 100 || "Max length is 100 characters!"
    ];

    let autosaveInterval;
    // This keeps a dispose method that Notify.create() returns
    let dismissNotification;

    const documentId = ref(null);
    const confirmRemovingFile = ref(false);
    const showCategories = ref(false);
    const schemeForm = ref(null);
    const callForm = ref(null);
    const fundingOrgSelect = ref(null);
    const adminOrgsSelect = ref(null);
    const advisingOrgsSelect = ref(null);
    const recipientOrgsSelect = ref(null);
    const sdgThemeSelect = ref(null);
    const expanded = ref(false);
    const parentSchemesOptions = ref([]);
    const filterThemesSDGOptions = ref(store.state.scheme.schemeSDGThemeTypes);
    const scopes = computed(() => store.state.scheme.scopes);
    const showSuccessfullyCreatedCallDialog = computed(
      () => store.state.scheme.showSuccessfullyCreatedCallDialog
    );
    const scheme = ref({
      selectedSchemeId: 0,
      selectedSchemeIdentifier: "",
      selectedSchemeName: "",
      selectedSchemeCreatedBy: "",
      selectedSchemeParentSchemeId: null,
      selectedParentScheme: null,
      selectedSchemeComplexityRating: 0,
      selectedSchemeChanceOfSuccessRating: 0,
      selectedSchemeTimeForApplicationRating: 0,
      selectedSchemeBudget: "",
      selectedSchemePublicationStatus: "",
      selectedLink: "",
      selectedDocument: null,
      selectedSchemeIsCall: null,
      selectedSchemeIsApplicationPeriod: null,
      selectedSchemeIntroduction: "",
      selectedSchemeBody: "",
      selectedTransBody: "",
      selectedTransCriteria: "",
      selectedTransApplicationProcedure: "",
      selectedTransIntroduction: "",
      selectedTransApplicationInformation: "",
      transFinancialinformation: "",
      selectedSchemeCriteria: "",
      selectedSchemeIsOBScheme: null,
      selectedSchemeOBTitle: "",
      selectedSchemeOBDescription: "",
      selectedSchemeMinimalAge: 0,
      selectedSchemeMaximalAge: 0,
      selectedSchemeMinAmount: 0,
      selectedSchemeMaxAmount: 0,
      selectedSchemeIsPortal: false,
      selectedSchemeIsAlwaysOpen: false,
      selectedSchemeIsEnglish: false,
      selectedSchemeHasEnglishCalls: false,
      selectedSchemeIsScience: false,
      selectedSchemeICTU: false,
      selectedSchemeStartDate: "",
      selectedSchemeEndDate: "",
      selectedSchemeCallStartDate: "",
      selectedSchemeCallEndDate: "",
      selectedSchemeFundingOrgs: [{}],
      selectedSchemeAdministratingOrgs: [{}],
      selectedSchemeAdvisingOrgs: [{}],
      selectedSchemeSDGThemes: [],
      selectedSchemeRecipientOrgs: [{}],
      selectedSchemeScope: null,
      selectedSchemeTerm: "",
      selectedSchemeFinancialInformation: "",
      selectedSchemeApplicationProcedure: "",
      selectedSchemeApplicationInformation: "",
      selectedSchemePortalBody: "",
      selectedSchemeUserStatusTags: [],
      selectedSchemeEducationLevelTags: [],
      publicationStatuses: [],
      applicationTerms: [],
      selectedSchemeAccronym: "",
      selectedSchemeArchivecode: "",
      selectedSchemeDuration: "",
      selectedSchemeHasRecurringDeadline: false,
      selectedSchemeRecurringStartingYear: 0,
      selectedSchemeRecurringYearsDuration: 0,
      selectedSchemeRecurringStartDate: "",
      selectedSchemeRecurringEndDate: "",
      selectedSchemeDocuments: [{}],
      selectedSchemeLocationsTags: [],
      selectedSchemeIsOneOffDeadline: false,
      schemeSDGThemeTypes: [],
      selectedSchemeLinks: [{}],
      originalSchemeStatus: {},
      selectedSchemeStatus: 0,
      locationGroupsSelected: [],
      selectedSchemeSME: "",
      selectedSchemeExclusion: "",
      selectedSchemeKeywordsTags: [],
      locationTagsSelectedIds: [],
      selectedSchemeAudienceTags: [],
      selectedSchemePurposesTags: [],
      selectedSchemeSchemeTypesTags: [],
      selectedSchemeIndustriesNewTags: [],
      selectedSchemeProjectTypesTags: [],
      selectedTransBodyDe: "",
      selectedTransCriteriaDe: "",
      selectedTransApplicationProcedureDe: "",
      selectedTransIntroductionDe: "",
      selectedTransApplicationInformationDe: ""
    });
    const filterOrganisationsAdministratingOptions = ref(
      store.state.organisation.organisationsList
    );
    const filterOrganisationsFundingOptions = ref(
      store.state.organisation.organisationsList
    );
    const filterOrganisationsAdvisingOptions = ref(
      store.state.organisation.organisationsList
    );
    const filterOrganisationsRecipientOptions = ref(
      store.state.organisation.organisationsList
    );

    const areSchemeChangesLoading = computed(
      () => store.state.scheme.areSchemeChangesLoading
    );
    const displayStatus = computed(() => {
      const status = scheme.value.selectedSchemeStatus == 0;
      return status ? "Unknown" : scheme.value.selectedSchemeStatus.label;
    });
    const selectedSchemeImportantLinks = computed(() => {
      return scheme.value.selectedSchemeLinks.filter(
        el =>
          el.documentType === EDocumentType.UsefulLinks ||
          el.documentType === null
      );
    });
    const selectedSchemeOtherLinks = computed(() => {
      return scheme.value.selectedSchemeLinks.filter(
        el =>
          el.documentType !== EDocumentType.UsefulLinks &&
          el.documentType !== null &&
          el.documentType !== EDocumentType.VisualPingLink
      );
    });
    const locationGroups = computed(
      () => store.state.tree.locationGroupsSelected
    );
    const sortedSchemeDocsAndLinks = computed(() => {
      var schemeDocsAndLinks = [
        ...store.state.scheme.selectedSchemeDocuments,
        ...selectedSchemeOtherLinks.value
      ];
      return schemeDocsAndLinks.sort((a, b) =>
        a.documentType > b.documentType ? 1 : -1
      );
    });
    const allOrganisations = computed(
      () => store.state.organisation.organisationsList
    );
    const schemeSME = computed(() => store.state.scheme.selectedSchemeSME);
    const schemeExclusion = computed(
      () => store.state.scheme.selectedSchemeExclusion
    );
    const schemeKeywordsTags = computed(
      () => store.state.scheme.selectedSchemeKeywordsTags
    );
    const locationTagsSelectedIds = computed(
      () => store.state.scheme.locationTagsSelectedIds
    );
    const schemeAudienceTags = computed(
      () => store.state.scheme.selectedSchemeAudienceTags
    );
    const schemePurposesTags = computed({
      get: () => store.state.scheme.selectedSchemePurposesTags
    });
    const schemeLocationsTags = computed(
      () => store.state.scheme.selectedSchemeLocationsTags
    );
    const schemeSchemeTypesTags = computed(
      () => store.state.scheme.selectedSchemeSchemeTypesTags
    );
    const schemeIndustriesNewTags = computed(
      () => store.state.scheme.selectedSchemeIndustriesNewTags
    );
    const schemeUserStatusTags = computed(
      () => store.state.scheme.selectedSchemeUserStatusTags
    );
    const schemeEducationLevelTags = computed(
      () => store.state.scheme.selectedSchemeEducationLevelTags
    );
    const schemeProjectTypesTags = computed(
      () => store.state.scheme.selectedSchemeProjectTypesTags
    );
    const showDiscontinuedModal = computed(
      () => store.state.scheme.showDiscontinuedModal
    );
    const hasCurrentOrFutureCallsAndApplicationPeriods = ref(false);
    const schemeStatusOptions = computed(() => {
      return store.state.scheme.schemeStatuses.map(status => ({
        ...status,
        disable:
          hasCurrentOrFutureCallsAndApplicationPeriods.value &&
          (status.value == ESchemeStatus.Closed ||
            status.value == ESchemeStatus.Discontinued)
      }));
    });
    const showAddLink = computed({
      get: () => store.state.scheme.showAddLink,
      set: value => store.commit("scheme/updateShowAddLink", value)
    });
    const showEditLink = computed({
      get: () => store.state.scheme.showEditLink,
      set: value => store.commit("scheme/updateShowEditLink", value)
    });
    const showAddDocument = computed({
      get: () => store.state.scheme.showAddDocument,
      set: value => store.commit("scheme/updateShowAddDocument", value)
    });
    const showEditDocument = computed({
      get: () => store.state.scheme.showEditDocument,
      set: value => store.commit("scheme/updateShowEditDocument", value)
    });
    const selectedLink = computed({
      get: () => store.state.scheme.selectedLink,
      set: value => store.commit("scheme/setSelectedLink", value)
    });
    const isCallOrApplicationPeriod = computed(
      () =>
        scheme.value.selectedSchemeIsCall ||
        scheme.value.selectedSchemeIsApplicationPeriod
    );
    const isEUCallJobScheme = computed(
      () => scheme.value.selectedSchemeCreatedBy === CREATE_EU_CALLS_JOB
    );
    const isEUPortalUpdateCopy = computed(
      () => !!scheme.value.selectedSchemeEUPortalUpdateOriginalCallId
    );
    const showParentSchemeSelect = computed(
      () =>
        isCallOrApplicationPeriod.value &&
        isEUCallJobScheme.value &&
        !scheme.value.selectedSchemeParentSchemeId
    );
    const filterParentSchemesFn = (val, update) => {
      update(async () => {
        parentSchemesOptions.value =
          (await SchemesServices.getParentSchemesForSelect(val)) ?? [];
      });
    };
    const schemeUpdates = computed({
      get: () => store.state.scheme.selectedSchemeUpdates
    });

    const isGetSchemeFinished = ref(false);

    const parseSchemeDate = value => {
      if (!value) return null;
      const date = moment(value, "DD-MM-YYYY", true);
      return date.isValid() ? date.startOf("day") : null;
    };

    const isCurrentOrFutureCallOrApplicationPeriod = callOrApplicationPeriod => {
      if (callOrApplicationPeriod.isAlwaysOpen) return true;

      const today = moment().startOf("day");
      const callStart = parseSchemeDate(callOrApplicationPeriod.callStart);
      const callEnd = parseSchemeDate(callOrApplicationPeriod.callEnd);

      return (
        (callStart && callStart.isAfter(today)) ||
        !callEnd ||
        !callEnd.isBefore(today)
      );
    };

    const updateCurrentOrFutureCallsAndApplicationPeriodsStatus = async () => {
      if (
        store.state.scheme.selectedSchemeIsCall ||
        store.state.scheme.selectedSchemeIsApplicationPeriod
      ) {
        hasCurrentOrFutureCallsAndApplicationPeriods.value = false;
        return;
      }

      await SchemesServices.getSchemeCallsAndApplicationPeriods(
        route.params.id
      );

      hasCurrentOrFutureCallsAndApplicationPeriods.value = store.state.scheme.selectedSchemeCallsAndApplicationPeriods.some(
        isCurrentOrFutureCallOrApplicationPeriod
      );
    };

    onMounted(async () => {
      await TagsService.getTagTypeTree("User Status", ETagType.UserStatus);
      await TagsService.getTagTypeTree(
        "Education Level",
        ETagType.EducationLevel
      );
      await SchemesServices.getSchemeById(route.params.id);
      await SchemesServices.getSchemeSelectedTags(
        route.params.id,
        "User Status",
        ETagType.UserStatus
      );
      await SchemesServices.getSchemeSelectedTags(
        route.params.id,
        "Education Level",
        ETagType.EducationLevel
      );
      await OrganisationsService.getAllOrganisationsList();
      await SchemesServices.getSMETypes();
      SchemesServices.getScopes();
      await SchemesServices.getTerms();
      await SchemesServices.getDocumentTypes();
      await SchemesServices.getPublicationStatuses();
      await SchemesServices.getSchemeSDGThemeTypes();
      await SchemesServices.getSchemeStatuses();
      await updateCurrentOrFutureCallsAndApplicationPeriodsStatus();
      autosaveInterval = setInterval(() => {
        if (isCallOrApplicationPeriod.value) handleCallFormSubmit(true);
        else handleSchemeFormSubmit(true);
      }, AUTOSAVE_INTERVAL_MILLISECONDS);

      scheme.value = { ...store.state.scheme };
      isGetSchemeFinished.value = true;
    });
    onBeforeUnmount(() => {
      clearInterval(autosaveInterval);
      if (dismissNotification) dismissNotification();
    });

    watch(
      () => scheme.value.selectedSchemeStatus,
      (newValue, oldValue) => {
        if (
          newValue == ESchemeStatus.Discontinued &&
          oldValue != ESchemeStatus.Discontinued &&
          scheme.value.originalSchemeStatus != ESchemeStatus.Discontinued
        ) {
          store.commit("scheme/updatedShowDiscontinuedModal", true);
        }
      }
    );
    watch(locationGroups, newValue => {
      scheme.value.locationGroupsSelected = newValue;
    });

    const removeDocument = async documentId => {
      await SchemesServices.removeDocument(
        scheme.value.selectedSchemeId,
        documentId
      );
    };
    const nameSubstring = name => {
      return String(name).slice(0, 150);
    };
    const download = async (documentId, documentName) => {
      await SchemesServices.downloadDocument(documentId).then(response => {
        var fileURL = window.URL.createObjectURL(
          new Blob([response.data], { type: response.data.type })
        );
        var a = document.createElement("a");
        a.setAttribute("style", "display:none;");
        document.body.appendChild(a);
        a.download = documentName;
        a.href = fileURL;
        a.target = "_blank";
        a.click();
        document.body.removeChild(a);
      });
    };
    const disableDialog = () => {
      store.commit("scheme/disableSuccessfullyCreatedCallDialog");
    };
    const removeLink = link => {
      const index = scheme.value.selectedSchemeLinks.indexOf(link);

      if (index > -1) scheme.value.selectedSchemeLinks.splice(index, 1);
    };
    const submitForm = async autosave => {
      scheme.value.id = route.params.id;
      scheme.value.selectedSchemeSME = schemeSME.value;
      scheme.value.selectedSchemeExclusion = schemeExclusion.value;
      scheme.value.selectedSchemeKeywordsTags = schemeKeywordsTags.value;
      scheme.value.locationTagsSelectedIds = locationTagsSelectedIds.value;
      scheme.value.selectedSchemeAudienceTags = schemeAudienceTags.value;
      scheme.value.selectedSchemePurposesTags = schemePurposesTags.value;
      scheme.value.selectedSchemeLocationsTags = schemeLocationsTags.value;
      scheme.value.selectedSchemeSchemeTypesTags = schemeSchemeTypesTags.value;
      scheme.value.selectedSchemeIndustriesNewTags =
        schemeIndustriesNewTags.value;
      scheme.value.selectedSchemeUserStatusTags = schemeUserStatusTags.value;
      scheme.value.selectedSchemeEducationLevelTags =
        schemeEducationLevelTags.value;
      scheme.value.selectedSchemeProjectTypesTags =
        schemeProjectTypesTags.value;
      scheme.value.schemeUpdates = schemeUpdates.value;

      // Submit changes
      try {
        await SchemesServices.editScheme(scheme.value, autosave);
        if (autosave) raisePositiveNotification();
        else await redirectAfterEUPortalUpdatePublish();
      } catch (e) {
        if (autosave) raiseWarningNotification(e.message);
      }
    };
    // When an EU Portal update copy is published (Draft -> Live) the back end merges it into the original
    // call and removes the copy, so navigate the editor to the original call (which now holds the updates).
    const redirectAfterEUPortalUpdatePublish = async () => {
      if (!isEUPortalUpdateCopy.value) return;

      const publicationStatus =
        typeof scheme.value.selectedSchemePublicationStatus === "string"
          ? scheme.value.selectedSchemePublicationStatus
          : scheme.value.selectedSchemePublicationStatus?.name;

      if (publicationStatus !== "Live") return;

      // Stop autosave so it can't fire against the now-deleted copy, then reload onto the original.
      clearInterval(autosaveInterval);
      await router.push({
        name: "EditScheme",
        params: {
          id: String(scheme.value.selectedSchemeEUPortalUpdateOriginalCallId)
        }
      });
      window.location.reload();
    };
    const handleFormSubmit = () => {
      if (isCallOrApplicationPeriod.value) handleCallFormSubmit();
      else handleSchemeFormSubmit();
    };
    const handleSchemeFormSubmit = async (autosave = false) => {
      if (!scheme.value.selectedSchemeFundingOrgs.length > 0) {
        SchemesServices.updateErrorModal("Select funding organisation(s)!");
      } else if (!schemeLocationsTags.value.length > 0) {
        SchemesServices.updateErrorModal(
          "Please select at least one location tag."
        );
      } else {
        const isFormValid = await schemeForm.value.validate();
        if (isFormValid) await submitForm(autosave);
        else if (autosave) raiseWarningNotification();
      }
    };
    const handleCallFormSubmit = async (autosave = false) => {
      const isFormValid = await callForm.value.validate();
      if (isFormValid) await submitForm(autosave);
      else if (autosave) raiseWarningNotification();
    };
    const setInput = () => {
      fundingOrgSelect.value.updateInputValue("");
      adminOrgsSelect.value.updateInputValue("");
      advisingOrgsSelect.value.updateInputValue("");
      recipientOrgsSelect.value.updateInputValue("");
      sdgThemeSelect.value.updateInputValue("");
    };
    const disableWarning = () => {
      store.commit("scheme/updatedShowDiscontinuedModal", false);
      store.commit("scheme/resetSchemeStatus");
    };
    const setDiscontinued = () => {
      store.commit("scheme/updateSchemeStatus", ESchemeStatus.Discontinued);
      store.commit("scheme/updatedShowDiscontinuedModal", false);
    };
    const filterFundingOrganisationsFn = (val, update) => {
      update(() => {
        if (val === "") {
          filterOrganisationsFundingOptions.value = allOrganisations.value;
        } else {
          const needle = val.toLowerCase();
          filterOrganisationsFundingOptions.value = allOrganisations.value.filter(
            v => v.name.toLowerCase().indexOf(needle) > -1
          );
        }
      });
    };
    const filterAdministratingOrganisationsFn = (val, update) => {
      update(() => {
        if (val === "") {
          filterOrganisationsAdministratingOptions.value =
            allOrganisations.value;
        } else {
          const needle = val.toLowerCase();
          filterOrganisationsAdministratingOptions.value = allOrganisations.value.filter(
            v => v.name.toLowerCase().indexOf(needle) > -1
          );
        }
      });
    };
    const filterAdvisingOrganisationsFn = (val, update) => {
      update(() => {
        if (val === "") {
          filterOrganisationsAdvisingOptions.value = allOrganisations.value;
        } else {
          const needle = val.toLowerCase();
          filterOrganisationsAdvisingOptions.value = allOrganisations.value.filter(
            v => v.name.toLowerCase().indexOf(needle) > -1
          );
        }
      });
    };
    const filterRecipientOrganisationsFn = (val, update) => {
      update(() => {
        if (val === "") {
          filterOrganisationsRecipientOptions.value = allOrganisations.value;
        } else {
          const needle = val.toLowerCase();
          filterOrganisationsRecipientOptions.value = allOrganisations.value.filter(
            v => v.name.toLowerCase().indexOf(needle) > -1
          );
        }
      });
    };
    const filterThemesSDGFn = (val, update) => {
      update(() => {
        if (val === "") {
          filterThemesSDGOptions.value = scheme.value.schemeSDGThemeTypes;
        } else {
          const needle = val.toLowerCase();
          filterThemesSDGOptions.value = scheme.value.schemeSDGThemeTypes.filter(
            v => v.name.toLowerCase().indexOf(needle) > -1
          );
        }
      });
    };
    const iconBasedOnExtension = extension => {
      switch (extension) {
        case ".pdf":
          return "las la-2x la-file-pdf";
        case ".doc":
          return "las la-2x la-file-word";
        case ".docx":
          return "las la-2x la-file-word";
        case ".xls":
          return "las la-2x la-file-excel";
        case ".xlsx":
          return "las la-2x la-file-excel";
        case ".csv":
          return "las la-2x la-file-csv";
        case ".txt":
          return "las la-2x la-file";
        default:
          return "las la-2x la-file";
      }
    };
    const raiseWarningNotification = msg => {
      const message = msg
        ? `Autosave failed: ${msg}`
        : "Autosave failed: Form contains invalid fields.";
      dismissNotification = NotificationService.createWarningNotification(
        message
      );
    };
    const raisePositiveNotification = () => {
      dismissNotification = NotificationService.createPositiveNotification(
        "Changes saved automatically."
      );
    };

    const updateSchemeSelectedDocument = item => {
      store.commit("scheme/setSelectedDocument", item);
    };

    return {
      updateSchemeSelectedDocument,
      isGetSchemeFinished,
      displayStatus,
      adminOrgsSelect,
      advisingOrgsSelect,
      recipientOrgsSelect,
      sdgThemeSelect,
      fundingOrgSelect,
      locationGroups,
      expanded,
      editorRef,
      filterThemesSDGOptions,
      allOrganisations,
      confirmRemovingFile,
      requiredFieldRule,
      maxLengthRule,
      documentId,
      filterOrganisationsFundingOptions,
      filterOrganisationsAdministratingOptions,
      filterOrganisationsAdvisingOptions,
      filterOrganisationsRecipientOptions,
      showCategories,
      selectedSchemeImportantLinks,
      selectedSchemeOtherLinks,
      sortedSchemeDocsAndLinks,
      schemeForm,
      callForm,
      scheme,
      areSchemeChangesLoading,
      showDiscontinuedModal,
      schemeStatusOptions,
      showAddLink,
      showEditLink,
      selectedLink,
      showAddDocument,
      showEditDocument,
      isCallOrApplicationPeriod,
      isEUCallJobScheme,
      isEUPortalUpdateCopy,
      showParentSchemeSelect,
      parentSchemesOptions,
      filterParentSchemesFn,
      schemeUpdates,
      removeDocument,
      removeLink,
      nameSubstring,
      disableDialog,
      handleFormSubmit,
      download,
      setDiscontinued,
      disableWarning,
      setInput,
      filterFundingOrganisationsFn,
      filterAdministratingOrganisationsFn,
      filterAdvisingOrganisationsFn,
      filterRecipientOrganisationsFn,
      filterThemesSDGFn,
      iconBasedOnExtension,
      scopes,
      showSuccessfullyCreatedCallDialog
    };
  }
};
</script>
